import {
  FrameCornerIcon,
  SparkleIcon,
  StackSampleIcon,
} from '@/app/common/icons/general'
import FamilyWizardLayout from '@/app/pages/family_application/FamilyWizardLayout.tsx'
import { Button } from '@/common/components/Button'
import { RootState } from '@/storage/store'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import useTaxableIncomeDocumentParams from '../hooks/useTaxableIncomeDocumentParams'
import { SkipVerification } from '../skip-verification/SkipVerification'

type Props = {
  onContinueClick: () => void
  name: string
  onCantFindClick: () => void
}

export const CaptureInstructionsPage = ({
  onContinueClick,
  name,
  onCantFindClick,
}: Props) => {
  const { info } = useTaxableIncomeDocumentParams()
  const { t: translate } = useTranslation()
  const selectedForm = info()
  const ProgramSettings = useSelector(
    (state: RootState) => state.ProgramSettings
  )
  const [skip, setSkip] = useState(false)

  if (skip) {
    return (
      <SkipVerification
        onCancel={() => {
          setSkip(false)
        }}
      />
    )
  }

  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        <h1
          id="income-verification-title"
          className="text-3xl text-center font-medium text-gray-900 mb-10 sm:mb-5"
        >
          {translate(info().code).replace('Form ', '')}{' '}
          {translate('capture_instructions')}
        </h1>
      </FamilyWizardLayout.Header>

      <FamilyWizardLayout.Content>
        {selectedForm.code === 'form_1040' && (
          <div className="text-xl font-medium text-gray-900 mb-5 sm:mb-5 text-center">
            <h2>
              {`${translate('we_need_a_picture')} ${ProgramSettings.data?.acceptable_income_year} ${translate('tax_form')}`}
            </h2>
          </div>
        )}

        <div className="flex justify-center">
          <img
            src={selectedForm.image}
            alt={translate(selectedForm.name)}
            className=" h-60 shadow-md"
          />
        </div>
        <p className="text-lg text-center mt-10 px-10">
          {translate(selectedForm.name)}
        </p>

        <div className="mt-6">
          <div className="flex items-center">
            <div className="w-5 flex justify-center">
              <FrameCornerIcon />
            </div>
            <p className="ml-4 text-sm">{translate('position_document')}</p>
          </div>
        </div>

        <div className="mt-6">
          <div className="flex items-center">
            <div className="w-5 flex justify-center">
              <StackSampleIcon />
            </div>
            <p className="ml-4 text-sm">{translate('place_it_on_flat')}</p>
          </div>
        </div>

        <div className="mt-6">
          <div className="flex items-center">
            <div className="w-5 flex justify-center">
              <SparkleIcon />
            </div>
            <p className="ml-4 text-sm">
              {translate('avoid_glare_shaking_and_blur')}
            </p>
          </div>
        </div>
      </FamilyWizardLayout.Content>
      <FamilyWizardLayout.ActionBar>
        <div className="w-full flex flex-col">
          <Button onClick={onContinueClick} className="rounded h-9 mb-5">
            {translate('continue')}
          </Button>

          {selectedForm.name === 'non_taxable' ? (
            <Button
              variant={'text'}
              size="text"
              onClick={() => {
                setSkip(true)
              }}
            >
              Skip income verification
            </Button>
          ) : (
            <Button
              className="text-sm leading-5 font-semibold text-primary bg-transparent hover:bg-transparent cursor-pointer"
              onClick={onCantFindClick}
            >
              {translate('cant_find_my')} {translate(name).replace('Form ', '')}
            </Button>
          )}
        </div>
      </FamilyWizardLayout.ActionBar>
    </FamilyWizardLayout>
  )
}
